import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { statsApi, type CompliancePeriod } from '../features/stats/api.ts';
import { termsApi } from '../features/terms/api.ts';
import { dv } from '../i18n/dv.ts';
import { formatDvDate } from '../lib/dvDate.ts';
import { type Body, bodyOfRole } from '../lib/body.ts';
import { BodyToggle } from '../features/orgSettings/BodyToggle.tsx';
import { useAuthStore } from '../store/auth.ts';

// A completed period's [start, end) is exclusive on the end; show the last
// included day (end − 1ms) for a human-friendly «start – end» range.
function periodRange(start: string, end: string): string {
  const endInclusive = new Date(new Date(end).getTime() - 1).toISOString();
  return `${formatDvDate(start)} ${dv.stats.compliance.rangeSep} ${formatDvDate(endInclusive)}`;
}

// Compliance level → a semantic M3 colour (success / warning / error) used for
// both the ring indicator and the headline text.
function levelClasses(pct: number): { color: string; text: string } {
  if (pct >= 90) return { color: 'hsl(var(--success))', text: 'text-[hsl(var(--success))]' };
  if (pct >= 70) return { color: 'hsl(var(--warning))', text: 'text-[hsl(var(--warning))]' };
  return { color: 'hsl(var(--destructive))', text: 'text-[hsl(var(--destructive))]' };
}

function HeatCell({ period, muted }: { period: CompliancePeriod; muted?: boolean }): JSX.Element {
  const base = 'size-4 rounded-sm';
  const cls = muted
    ? 'border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--surface-container-highest))]'
    : period.met
      ? 'bg-[hsl(var(--success))]'
      : 'bg-[hsl(var(--destructive))]';
  const label = muted
    ? `${periodRange(period.start, period.end)} · ${dv.stats.compliance.currentPeriod}`
    : periodRange(period.start, period.end);
  return <span className={`${base} ${cls}`} title={label} aria-label={label} />;
}

// Compliance gauge — a Material 3 determinate ring: a rounded active arc
// separated from the remaining track by a small gap, coloured by level.
function ComplianceRing({ pct, color }: { pct: number; color: string }): JSX.Element {
  const SIZE = 116;
  const SW = 11;
  const R = (SIZE - SW) / 2;
  const C = 2 * Math.PI * R;
  const p = Math.max(0, Math.min(100, pct));
  const dash = (p / 100) * C;
  const GAP = 8; // px arc gap between indicator and track (M3 determinate circular)
  const trackLen = Math.max(0, C - dash - 2 * GAP);
  const cx = SIZE / 2;
  const trackAngle = -90 + ((dash + GAP) / C) * 360;
  return (
    <div className="relative shrink-0" style={{ width: SIZE, height: SIZE }}>
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden>
        {trackLen > 0 && (
          <circle
            cx={cx} cy={cx} r={R} fill="none"
            stroke="hsl(var(--surface-container-highest))" strokeWidth={SW} strokeLinecap="round"
            strokeDasharray={`${trackLen} ${C}`} transform={`rotate(${trackAngle} ${cx} ${cx})`}
          />
        )}
        {dash > 0 && (
          <circle
            cx={cx} cy={cx} r={R} fill="none"
            stroke={color} strokeWidth={SW} strokeLinecap="round"
            strokeDasharray={`${dash} ${C}`} transform={`rotate(-90 ${cx} ${cx})`}
            style={{ transition: 'stroke-dasharray 0.6s ease' }}
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="tabular text-3xl font-semibold" style={{ color }} dir="ltr">{pct}%</span>
      </div>
    </div>
  );
}

export function StatsCompliance(): JSX.Element {
  // Default to the viewer's own body when they're elected, else COUNCIL.
  const role = useAuthStore((s) => s.user?.role);
  const [body, setBody] = useState<Body>(() => (role ? bodyOfRole(role) : null) ?? 'COUNCIL');

  const currentYear = new Date().getFullYear();

  // Dhauru selector — defaults to the body's active term. The year dropdown
  // lists only the years that fall under the selected dhauru (its date span,
  // capped at the current year so future, data-less years aren't offered).
  const termsQ = useQuery({ queryKey: ['terms', body], queryFn: () => termsApi.list(body) });
  const terms = useMemo(() => termsQ.data ?? [], [termsQ.data]);
  const [termId, setTermId] = useState<string>('');
  useEffect(() => {
    if (!terms.length) { setTermId(''); return; }
    setTermId(terms.find((t) => t.isActive)?.id ?? terms[0]!.id);
  }, [terms]);

  const years = useMemo(() => {
    const term = terms.find((t) => t.id === termId);
    const startY = term?.startDate ? new Date(term.startDate).getFullYear() : currentYear;
    const endY = Math.min(term?.endDate ? new Date(term.endDate).getFullYear() : currentYear, currentYear);
    const out: number[] = [];
    for (let y = Math.max(endY, startY); y >= startY; y--) out.push(y); // newest first
    return out.length ? out : [currentYear];
  }, [terms, termId, currentYear]);

  // Keep the selected year within the dhauru's available range (default newest).
  const [year, setYear] = useState<number>(currentYear);
  useEffect(() => {
    if (!years.includes(year)) setYear(years[0]!);
  }, [years]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = useQuery({
    queryKey: ['stats', 'compliance', body, year],
    queryFn: () => statsApi.compliance(body, year),
  });
  const data = q.data;

  // Toolbar band — body + dhauru + year (matches the other statistics tabs).
  const toggle = (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-3 sm:px-4">
      <div className="flex flex-wrap items-center gap-2">
        <BodyToggle body={body} onChange={setBody} />
        {terms.length > 0 && (
          <select
            aria-label={dv.meetings.term}
            value={termId}
            onChange={(e) => setTermId(e.target.value)}
            className="select w-auto rounded-full py-1.5"
          >
            {terms.map((t) => (
              <option key={t.id} value={t.id}>{t.number} ވަނަ ދައުރު</option>
            ))}
          </select>
        )}
        <select
          aria-label={dv.stats.compliance.year}
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          dir="ltr"
          className="select w-auto rounded-full py-1.5"
        >
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>
    </div>
  );

  if (q.isLoading || !data) {
    return (
      <div className="space-y-6">
        {toggle}
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
          {dv.common.loading}
        </p>
      </div>
    );
  }

  // Not configured — prompt the user to set the requirement in settings.
  if (data.cadence === null) {
    return (
      <div className="space-y-6">
        {toggle}
        <section className="space-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-6 text-center">
          <p className="text-sm">{dv.stats.compliance.notConfigured}</p>
          <p className="text-xs text-muted-foreground">{dv.stats.compliance.notConfiguredHelp}</p>
          <Link to="/settings/org" className="btn-primary inline-block">
            {dv.stats.compliance.configureLink}
          </Link>
        </section>
      </div>
    );
  }

  const pct = data.percentage ?? 0;
  const lvl = levelClasses(pct);
  const cadenceLabel = dv.stats.compliance.cadenceLabel[data.cadence];
  const periods = data.periods ?? [];
  const missed = data.missed ?? [];

  return (
    <div className="space-y-6">
      {toggle}
      {/* Headline card — summary on the reading side, gauge on the left */}
      <section className="flex flex-row items-center justify-between gap-4 rounded-3xl bg-[hsl(var(--chrome))] p-5 sm:gap-6 lg:p-6">
        <div className="min-w-0 flex-1 space-y-1.5">
          <h2 className="status-label text-muted-foreground">{dv.stats.compliance.heading}</h2>
          <p className="flex flex-wrap items-baseline gap-x-2 text-base">
            <span className="font-medium text-foreground">{cadenceLabel}</span>
            <span className="text-muted-foreground" aria-hidden>·</span>
            <span className="tabular text-muted-foreground" dir="ltr">{data.year}</span>
          </p>
          <p className={`text-sm font-medium ${lvl.text}`}>
            {dv.stats.compliance.heldOfRequired(data.metPeriods ?? 0, data.totalPeriods ?? 0)}
          </p>
          {/* Inline legend for the heatmap below. */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-0.5 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><span className="size-2.5 rounded-sm bg-[hsl(var(--success))]" aria-hidden />{dv.stats.compliance.held}</span>
            <span className="inline-flex items-center gap-1.5"><span className="size-2.5 rounded-sm bg-[hsl(var(--destructive))]" aria-hidden />{dv.stats.compliance.missedKpi}</span>
          </div>
        </div>
        <ComplianceRing pct={pct} color={lvl.color} />
      </section>

      {/* KPI tiles */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: dv.stats.compliance.required, value: data.totalPeriods ?? 0 },
          { label: dv.stats.compliance.held, value: data.metPeriods ?? 0 },
          { label: dv.stats.compliance.missedKpi, value: data.missedPeriods ?? 0 },
        ].map((k) => (
          <div key={k.label} className="flex flex-col gap-1 rounded-3xl bg-[hsl(var(--chrome))] p-4 text-center sm:p-5">
            <span className="tabular text-2xl font-bold text-foreground sm:text-3xl" dir="ltr">{k.value}</span>
            <span className="text-[11px] leading-tight text-muted-foreground sm:text-xs">{k.label}</span>
          </div>
        ))}
      </div>

      {/* Heatmap strip */}
      <section className="space-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
        <h2 className="status-label text-muted-foreground">{dv.stats.compliance.heatmapHeading}</h2>
        <div className="flex flex-wrap gap-1">
          {periods.map((p) => (
            <HeatCell key={p.start} period={p} />
          ))}
          {data.currentPeriod && <HeatCell period={data.currentPeriod} muted />}
        </div>
      </section>

      {/* Missed-periods list */}
      <section className="overflow-hidden rounded-3xl bg-[hsl(var(--chrome))]">
        <h2 className="status-label border-b border-black/[0.05] px-4 py-3 text-muted-foreground sm:px-5">
          {dv.stats.compliance.missedHeading}
        </h2>
        {missed.length === 0 ? (
          <p className="p-6 text-center text-sm text-[hsl(var(--success))]">{dv.stats.compliance.allMet}</p>
        ) : (
          <ul className="divide-y divide-black/[0.05]">
            {missed.map((m) => (
              <li key={m.start} className="px-4 py-3 text-sm text-muted-foreground sm:px-5">
                {periodRange(m.start, m.end)}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
